import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

/* ---- Bottom Tab Bar ---- */

export const BottomTabBar = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  height: calc(56px + env(safe-area-inset-bottom, 0px));
  padding-bottom: env(safe-area-inset-bottom, 0px);
  background: ${({ theme }) => theme.colors['neutral-0']};
  border-top: 1px solid hsla(0, 0%, 0%, 0.08);
  display: flex;
  align-items: stretch;
`;

export const TabItem = styled(Link)<{ $isActive?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  text-decoration: none;
  color: ${({ $isActive, theme }) =>
    $isActive ? theme.colors['primary-60'] : theme.colors['neutral-60']};
  font-size: 10px;
  font-weight: ${({ $isActive }) => ($isActive ? '600' : '500')};
  transition: color 200ms cubic-bezier(0.2, 0.8, 0.4, 1);
  -webkit-tap-highlight-color: transparent;

  svg {
    width: 22px;
    height: 22px;
  }

  &:active {
    color: ${({ theme }) => theme.colors['primary-60']};
  }
`;

export const MenuTabButton = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors['neutral-60']};
  font-size: 10px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: color 200ms cubic-bezier(0.2, 0.8, 0.4, 1);
  -webkit-tap-highlight-color: transparent;

  svg {
    width: 22px;
    height: 22px;
  }

  &:active {
    color: ${({ theme }) => theme.colors['primary-60']};
  }
`;

/* ---- Drawer Overlay ---- */

const slideIn = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const DrawerBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 20;
  background: rgba(0, 0, 0, 0.3);
  animation: ${fadeIn} 200ms cubic-bezier(0.2, 0.8, 0.4, 1);
  -webkit-tap-highlight-color: transparent;
`;

export const DrawerPanel = styled.aside`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 21;
  width: 280px;
  max-width: 85vw;
  background: ${({ theme }) => theme.colors['neutral-0']};
  box-shadow: 0 8px 24px hsla(0, 0%, 0%, 0.16);
  animation: ${slideIn} 300ms cubic-bezier(0.2, 0.8, 0.4, 1);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

export const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid hsla(0, 0%, 0%, 0.08);
`;

export const DrawerLogo = styled.img`
  height: 28px;
`;

export const DrawerServiceLabel = styled.div`
  padding: 16px 20px 8px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: ${({ theme }) => theme.colors['neutral-70']};
`;

export const DrawerNavItem = styled(Link)<{ $isSelected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 20px;
  text-decoration: none;
  font-size: 15px;
  font-weight: ${({ $isSelected }) => ($isSelected ? '600' : '400')};
  color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors['primary-60'] : theme.colors['neutral-100']};
  background: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors['secondary-20'] : 'transparent'};
  transition: background-color 200ms cubic-bezier(0.2, 0.8, 0.4, 1);
  -webkit-tap-highlight-color: transparent;

  svg {
    width: 20px;
    height: 20px;
    color: ${({ $isSelected, theme }) =>
      $isSelected ? theme.colors['primary-60'] : theme.colors['neutral-60']};
  }

  &:active {
    background: ${({ theme }) => theme.colors['neutral-20']};
  }
`;

export const DrawerDivider = styled.div`
  height: 1px;
  background: hsla(0, 0%, 0%, 0.08);
  margin: 8px 20px;
`;

export const DrawerServicesSection = styled.div`
  padding: 8px 20px;
`;

export const DrawerServiceCard = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 0;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors['neutral-100']};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  text-align: left;

  &:active {
    opacity: 0.7;
  }
`;

export const DrawerServiceIcon = styled.div<{ $color: string }>`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: ${({ $color }) => `${$color}14`};

  svg {
    width: 18px;
    height: 18px;
    color: ${({ $color }) => $color};
  }
`;
